//TODO : Refactor after ai
/**
 * Zugriff auf das Viacid-Modell und die Beispieldaten.
 *
 * Alle Screens fragen hier nach Objektklassen, Datensätzen, Beziehungen und
 * erlaubten Actions – kein Screen kennt eine einzelne Objektklasse. Die
 * Funktionen sind rein: Der veränderliche Bestand lebt in `app/store/daten`
 * und wird hereingereicht.
 */

import { DATENSAETZE } from "./daten.generated";
import { OBJEKTKLASSEN, RELATIONEN, ROLLEN } from "./model.generated";
import type {
  Aktion,
  Datensatz,
  Navigationsbereich,
  ObjektKlasse,
  Relation,
  Rolle,
} from "./types";

export * from "./types";
export * from "./ablaeufe";
export * from "./folgeaktionen";
export { formatWert, statusFarbe, ERLEDIGTE_ZUSTAENDE } from "./format";
export { DEMO_LOGIN } from "./demoLogin";

/** Bestand aller Datensätze, je Objektklasse (Slug). */
export type Datenbestand = Record<string, Datensatz[]>;

export const STARTBESTAND: Datenbestand = DATENSAETZE;

export const NAVIGATIONSBEREICHE: { key: Navigationsbereich; label: string }[] =
  [
    { key: "vorgaenge", label: "Vorgänge" },
    { key: "dokumente", label: "Dokumente" },
    { key: "stammdaten", label: "Stammdaten" },
  ];

export function alleKlassen(): ObjektKlasse[] {
  return OBJEKTKLASSEN;
}

export function objektKlasse(slug: string): ObjektKlasse | undefined {
  return OBJEKTKLASSEN.find((k) => k.slug === slug);
}

export function klasseNachName(name: string): ObjektKlasse | undefined {
  return OBJEKTKLASSEN.find((k) => k.name === name);
}

export function klassenImBereich(bereich: Navigationsbereich): ObjektKlasse[] {
  return OBJEKTKLASSEN.filter(
    (k) => k.navigation === bereich && k.inNavigation
  ).sort((a, b) => a.prioritaet - b.prioritaet);
}

export function alleRollen(): Rolle[] {
  return ROLLEN;
}

export function rolle(kuerzel: string): Rolle | undefined {
  return ROLLEN.find((r) => r.kuerzel === kuerzel);
}

export function relation(id: string): Relation | undefined {
  return RELATIONEN.find((r) => r.id === id);
}

export function datensaetze(bestand: Datenbestand, slug: string): Datensatz[] {
  return bestand[slug] ?? [];
}

export function datensatz(
  bestand: Datenbestand,
  slug: string,
  id: string
): Datensatz | undefined {
  return datensaetze(bestand, slug).find((d) => d.id === id);
}

export const alsListe = (wert: string | string[] | undefined): string[] =>
  wert == null ? [] : Array.isArray(wert) ? wert : [wert];

/** Beziehungen, die von dieser Klasse ausgehen. */
export function ausgehendeRelationen(slug: string): Relation[] {
  return RELATIONEN.filter((r) => r.von === slug);
}

/** Beziehungen anderer Klassen, die auf diese Klasse zeigen. */
export function eingehendeRelationen(slug: string): Relation[] {
  return RELATIONEN.filter((r) => r.zu === slug);
}

/** Die Datensätze, auf die `quelle` über `rel` verweist. */
export function bezogene(
  bestand: Datenbestand,
  rel: Relation,
  quelle: Datensatz
): Datensatz[] {
  return alsListe(quelle.bezuege[rel.id])
    .map((id) => datensatz(bestand, rel.zu, id))
    .filter((d): d is Datensatz => d != null);
}

/** Die Datensätze der Quellklasse, die über `rel` auf `zielId` verweisen. */
export function verweisende(
  bestand: Datenbestand,
  rel: Relation,
  zielId: string
): Datensatz[] {
  return datensaetze(bestand, rel.von).filter((d) =>
    alsListe(d.bezuege[rel.id]).includes(zielId)
  );
}

/** Ob ein Datensatz über irgendeine Beziehung mit dem Mitarbeiter `personaId` verbunden ist. */
export function gehoertZu(
  slug: string,
  satz: Datensatz,
  personaId: string
): boolean {
  if (slug === "mitarbeiter") return satz.id === personaId;
  return ausgehendeRelationen(slug)
    .filter((r) => r.zu === "mitarbeiter")
    .some((r) => alsListe(satz.bezuege[r.id]).includes(personaId));
}

/** Actions, mit denen ein Datensatz entsteht – sie stehen an der Übersicht, nicht am Datensatz. */
function erzeugt(klasse: ObjektKlasse, a: Aktion): boolean {
  return (
    a.operation === "Anlegen" ||
    (a.erlaubt.length === 0 && a.ziel != null && a.ziel === klasse.zustaende[0])
  );
}

/** Die Actions, die eine Rolle an diesem Datensatz im aktuellen Zustand ausführen darf. */
export function erlaubteAktionen(
  klasse: ObjektKlasse,
  satz: Datensatz | null,
  rollenKuerzel: string
): Aktion[] {
  return klasse.actions.filter(
    (a) =>
      a.rollen.includes(rollenKuerzel) &&
      (satz == null
        ? a.operation === "Anlegen" ||
          (a.operation === "Empfangen" && a.ziel != null)
        : !erzeugt(klasse, a) &&
          (a.erlaubt.length === 0 ||
            (satz.status != null && a.erlaubt.includes(satz.status))))
  );
}

/** Die Action, mit der ein neuer Datensatz dieser Klasse entsteht (für die Rolle). */
export function anlegeAktion(
  klasse: ObjektKlasse,
  rollenKuerzel: string
): Aktion | undefined {
  return erlaubteAktionen(klasse, null, rollenKuerzel)[0];
}

/**
 * Überschrift einer Beziehung in Kundensprache. Zeigen zwei Beziehungen
 * derselben Klasse auf dasselbe Ziel (Einsatz: Team und Gruppenführer),
 * kommt die fachliche Aussage dazu.
 */
export function relationsTitel(
  rel: Relation,
  richtung: "ausgehend" | "eingehend"
): string {
  if (richtung === "ausgehend") {
    if (rel.von === rel.zu)
      return rel.label.charAt(0).toUpperCase() + rel.label.slice(1);
    const ziel = objektKlasse(rel.zu);
    const doppelt =
      RELATIONEN.filter((r) => r.von === rel.von && r.zu === rel.zu).length > 1;
    const name = rel.zuViele ? ziel?.plural : ziel?.name;
    return doppelt ? `${name} (${rel.label})` : (name ?? rel.label);
  }
  const quelle = objektKlasse(rel.von);
  const doppelt =
    rel.von === rel.zu ||
    RELATIONEN.filter((r) => r.von === rel.von && r.zu === rel.zu).length > 1;
  return doppelt
    ? `${quelle?.plural} (${rel.label})`
    : (quelle?.plural ?? rel.name);
}
